import { Box, Flex } from "@chakra-ui/react";
import { Modal } from "antd";
import { useLoading } from "components/GlobalLoading";
import InputFile from "components/Input/InputFile";
import useImportarConsumoUnidades from "core/features/consumo/hooks/useImportarConsumoUnidades";
import { ImportarConsumoUnidades } from "core/features/consumo/typings";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import FooterModal from "ui/components/Button/FooterModal";
import FormUnidades from "./FormUnidades";
import PrimaryButton from "ui/components/Button/PrimaryButton";
import { useCurrentTenant } from "core/features/auth/tenant.store";
import useUnidadesSimplificado from "core/features/unidades/useUnidadesSimplificado";
import { exportExcelExemploImportacaoConsumo } from "../../../util";
import excelToJson from "infra/helpers/excelToJson";
import { showError, showSuccess } from "infra/helpers/alerta";
interface ModalImportarExcelConsumoProps {
  open: boolean;
  onClose: () => any;
  id?: string;
  refetchList?: () => any;
}

export default function ModalImportarExcelConsumo({
  onClose,
  open,
  id,
  refetchList,
}: ModalImportarExcelConsumoProps) {
  const { tenant } = useCurrentTenant();
  const { mutate: saveConsumo, isLoading: isLoadingSave } =
    useImportarConsumoUnidades(id!);
  const { control, handleSubmit, reset, setValue, watch } =
    useForm<ImportarConsumoUnidades>({
      defaultValues: {
        unidades: {},
      },
    });
  const { data: allUnidades, isLoading: isLoadingAllUnidades } =
    useUnidadesSimplificado(tenant.id, {
      limit: 99999,
    });

  const allUnidadesNumeros = useMemo(
    () =>
      allUnidades?.items.map((item) =>
        item.blocoQuadra
          ? `${item.blocoQuadra.nome.trim()}-${item.numero.trim()}`
          : item.numero.trim()
      ),
    [allUnidades]
  );

  const allUnidadesNumeroIdMap = useMemo(
    () =>
      allUnidades?.items.reduce((acc, next) => {
        if (next.blocoQuadra) {
          acc[`${next.blocoQuadra?.nome.trim()}-${next.numero.trim()}`] =
            next.id;
        } else {
          acc[next.numero.trim()] = next.id;
        }
        return acc;
      }, {} as Record<string, string>),
    [allUnidades]
  );
  const onSubmit = async (data: ImportarConsumoUnidades) => {
    const _unidades = Object.entries(data.unidades).reduce(
      (acc, [key, value]) => {
        if (value && Number(value) > 0) {
          acc[key] = value;
        }
        return acc;
      },
      {} as Record<string, number>
    );

    const _data = { ...data, unidades: _unidades };

    saveConsumo(_data, {
      onSuccess: () => {
        refetchList?.();
        setTimeout(onClose, 150);
      },
    });
  };

  useEffect(() => {
    if (open) {
      reset({
        unidades: {},
      });
    }
  }, [reset, open]);

  useLoading(isLoadingSave || isLoadingAllUnidades);

  const onImportExcel = async (file: File) => {
    const json = await excelToJson<
      {
        medidaAtual: string | number;
        numero: string;
        blocoQuadra?: string;
        index: string;
      },
      keyof {
        medidaAtual: string | number;
        numero: string;
        blocoQuadra?: string;
      }
    >(file);

    for (let i = 0; i < json.length; i++) {
      const row = json[i];
      if (
        !allUnidadesNumeros?.includes(
          row.blocoQuadra ? `${row.blocoQuadra}-${row.numero}` : row.numero
        )
      ) {
        return showError(`A unidade informada na linha ${i + 2} é inválida.`);
      }

      if (row.medidaAtual && !Number.isFinite(Number(row.medidaAtual))) {
        return showError(
          `O consumo informado na linha ${i + 2} não é um número válido`
        );
      }
    }

    const _unidadesFormMap = json.reduce((acc, next) => {
      if (allUnidadesNumeroIdMap) {
        const numero = String(next.numero);

        let id = allUnidadesNumeroIdMap[numero.trim()];
        if (next.blocoQuadra) {
          id =
            allUnidadesNumeroIdMap[`${next.blocoQuadra}-${numero.trim()}`];
        }
        acc[id] = next.medidaAtual ? Number(next.medidaAtual) : 0;
      }

      return acc;
    }, {} as Record<string, number>);

    setValue("unidades", _unidadesFormMap);

    showSuccess("Planilha importada com sucesso!");
  };

  const form = watch();

  const _unidadesLista = useMemo(
    () => allUnidades?.items?.filter((unidade) => !!form.unidades[unidade.id]),
    [allUnidades?.items, form.unidades]
  );

  return (
    <Modal
      visible={open}
      onCancel={onClose}
      destroyOnClose
      width="700px"
      title={`${id ? "Editar" : "Cadastrar"} Consumo`}
      footer={
        <FooterModal
          showRegisterAnother={false}
          isLoadingSave={isLoadingSave}
          onClose={onClose}
          onSubmit={handleSubmit(onSubmit)}
        />
      }
    >
      <form onSubmit={(ev) => handleSubmit(onSubmit)(ev)}>
        <Box>
          <Flex>
            <InputFile
              key={new Date().getTime()}
              onChange={(file) => {
                file && onImportExcel(file);
              }}
              label="Importar Excel"
              accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
              value={null}
              showPreview={false}
            />

            {!isLoadingAllUnidades && allUnidades?.items?.length ? (
              <PrimaryButton
                w="250px"
                ml="auto"
                bgColor="green.500"
                onClick={() =>
                  exportExcelExemploImportacaoConsumo(allUnidades.items)
                }
              >
                Baixar planilha exemplo
              </PrimaryButton>
            ) : null}
          </Flex>

          <Box mt={4}>
            <FormUnidades
              control={control}
              setValue={setValue}
              allUnidades={_unidadesLista ?? []}
            />
          </Box>
        </Box>
      </form>
    </Modal>
  );
}
